/**
 * <card-video> — autoplays a product's video on its collection card while the
 * card is on screen, and pauses it once it scrolls away. The source is only
 * attached the first time the card comes into view, so off-screen cards cost
 * nothing. Being a custom element, cards injected later (filters, sorting,
 * pagination) upgrade on their own.
 */
if (!customElements.get('card-video')) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const viewportObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        entry.target.isOnScreen = entry.isIntersecting;
        entry.target.update();
      });
    },
    { threshold: 0.5 }
  );

  customElements.define(
    'card-video',
    class CardVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video || !this.dataset.src) return;

        this.isOnScreen = false;
        this.imageSwapped = false;
        this.video.muted = true;
        this.video.addEventListener('playing', () => this.classList.add('is-playing'));

        this.watchCardImage();
        viewportObserver.observe(this);
      }

      disconnectedCallback() {
        viewportObserver.unobserve(this);
        this.imageObserver?.disconnect();
      }

      // Swatch King swaps the card image when a colour is picked. The video
      // would hide that, so step aside while a different image is showing and
      // come back when the original colour is selected again.
      watchCardImage() {
        const media = this.parentElement?.querySelector('.media');
        const image = media?.querySelector('img');
        if (!image) return;

        const originalSrc = image.getAttribute('src');
        this.imageObserver = new MutationObserver(() => {
          const current = media.querySelector('img');
          this.imageSwapped = !!current && current.getAttribute('src') !== originalSrc;
          this.update();
        });
        this.imageObserver.observe(media, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['src', 'srcset'],
        });
      }

      update() {
        const shouldPlay = this.isOnScreen && !this.imageSwapped && !reducedMotion.matches;
        this.classList.toggle('is-hidden', this.imageSwapped);

        if (!shouldPlay) {
          this.video.pause();
          return;
        }

        if (!this.video.getAttribute('src')) {
          this.video.src = this.dataset.src;
          this.video.preload = 'auto';
        }
        // Autoplay can still be refused (e.g. iOS Low Power Mode); the image stays visible then.
        this.video.play()?.catch(() => {});
      }
    }
  );
}
